import {Dispatch, SetStateAction} from 'react';
import {ListGoals_goals_contact} from '../../../schema-types/ListGoals';
import {UpdateGoalVariables} from '../../../schema-types/UpdateGoal';
import {getCurrencyString} from '../../../utils/currencyFormatter';
import {shortenName} from '../../../utils/shortenName';
import {cleanForCuro} from '../../../utils/cleanString';
import {
  CAPITAL_ID,
  CAPITAL_RATE_ID,
  INCOME_ID,
  INCOME_RATE_ID,
  OBJECTIVE_CAPITAL,
  OBJECTIVE_INCOME,
  OWNER_FAMILY,
  OWNER_FAMILY_LABEL,
  OWNER_JOINT,
  OWNER_PRIMARY,
  OWNER_SECONDARY,
  OWNER_TRUST,
  PURPOSE_RETIREMENT,
} from './constants';
import {YEAR_IN_MILISECS} from '../../../utils/dateUtils';
import {StatusAtReviewEnum} from '../../../schema-types/globalTypes';
import {newUTCDate} from '../../../utils/newUTCDate';

import {AccountBasicFragment} from '../../../schema-types/AccountBasicFragment';
import OWNERSHIP from '../../../constants/Ownership';
import {getISODateOrUndefined} from '../../../utils/getISODate';
import validateUUID from '../../../utils/validateUUID';

export const dateFormatter = (
  value: string | number | Date,
  options: Intl.DateTimeFormatOptions = {year: 'numeric', month: 'short'}
) =>
  typeof value === 'string'
    ? newUTCDate(value).toLocaleDateString('en-gb', options)
    : 'N/A';

export const camelCaseToSpacedString = (value: string) =>
  value.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase());

export interface GoalFormValues {
  id: string;
  ownerName: string;
  planningObjective: string | null;
  planningObjectivePurpose: string | null;
  targetEndDate: Date | string | number;
  [INCOME_ID]: number;
  [CAPITAL_ID]: number;
  [INCOME_RATE_ID]: number;
  [CAPITAL_RATE_ID]: number;
  targetAge: number | null;
  ownershipRadio: string | null;
  drawdownRate: number;
  targetYears: string | number | null;
  name: string | null;
  ownership: OWNERSHIP | null;
  description: string | null;
  contactId: string | null;
  goalType: string | null;
  priority: string | null;
  secondaryContactId: string | null;
  statusAtReview: StatusAtReviewEnum;
  objectiveType: string | null;
  title: string | null;
  contact: ListGoals_goals_contact;
  secondaryContact: ListGoals_goals_contact;
}

export interface GoalPartProps {
  setRequireValidation: Dispatch<SetStateAction<string[]>>;
  setGoalFormName: (x: string) => void;
}

export const getPlanningObjectiveName = (planningObjective: string) => {
  switch (planningObjective) {
    case OBJECTIVE_INCOME:
      return 'generate an income';
    case OBJECTIVE_CAPITAL:
      return 'build a pot';
    case 'protection':
      return 'be protected against adverse life events';
    case 'estatePlanning':
      return 'mitigate inheritance tax liabilities';
    case 'taxPlanning':
      return 'reduce tax liabilities';
    case 'financialPlanning':
      return 'consolidate investments';
    default:
      return '';
  }
};

export const getPlanningObjectivePurposeName = (planningObjectivePurpose: string) => {
  switch (planningObjectivePurpose) {
    case 'retirement':
      return ' to fund retirement';
    case 'debt':
      return ' to pay down debt';
    case 'education':
      return ' to pay for education';
    case 'property':
      return ' to buy a property';
    case 'emergency':
      return ' to build up an emergency fund';
    case 'gift':
      return ' to gift to beneficiaries';
    default:
      return '';
  }
};

export const getGoalFormName = (values: GoalFormValues): string => {
  const {
    ownerName = '',
    planningObjective,
    planningObjectivePurpose = '',
    targetEndDate,
    targetIncome,
    targetCapital,
    targetAge,
    ownership,
    targetYears,
  } = values;
  const formattedName = shortenName(ownerName);
  const isSingleLives = ownership === OWNERSHIP.SINGLE_LIVES;
  const isRetirement = planningObjectivePurpose === PURPOSE_RETIREMENT;
  const verb = isSingleLives ? ' wants to ' : ' want to ';
  const purposeLeftColumn = getPlanningObjectiveName(planningObjective!);
  const hasPurpose =
    values.planningObjective === OBJECTIVE_CAPITAL ||
    values.planningObjective === OBJECTIVE_INCOME;
  const purposeRightColumn = hasPurpose
    ? getPlanningObjectivePurposeName(planningObjectivePurpose!)
    : '';
  const moneyGBP = cleanForCuro(
    getCurrencyString(
      planningObjective === OBJECTIVE_INCOME ? targetIncome : targetCapital,
      {decimalPlaces: 0}
    )
  );
  const amount =
    moneyGBP && moneyGBP !== '' && moneyGBP !== 'GBP 0' ? ` of ${moneyGBP}` : '';
  const dateValue = isRetirement ? targetAge : dateFormatter(targetEndDate!);
  let ageOrDate = '';
  if (dateValue && dateValue !== 'N/A') {
    if (isRetirement && isSingleLives) {
      ageOrDate = ` at age ${targetAge}`;
    } else if (hasPurpose) {
      const options: Intl.DateTimeFormatOptions =
        parseInt(targetYears as string, 10) <= 2
          ? {year: 'numeric', month: 'long'}
          : {year: 'numeric', month: undefined};

      ageOrDate = ` by ${dateFormatter(targetEndDate!, options)}`;
    }
  }

  let result = `${formattedName}${verb}${purposeLeftColumn}${amount}${purposeRightColumn}${ageOrDate}`;

  if (result.length > 100) {
    // making sure there's enough space for the ellipses
    result = `${result.slice(0, 97)}...`;
  }

  return result;
};

export const getOwnerName = (
  value: string | null,
  account: AccountBasicFragment | undefined
): string => {
  const goalContacts = account?.contacts;
  let name: string;
  const trustName = account?.name ?? '';
  const primaryContact =
    goalContacts?.find((c) =>
      c.accountContacts.find((ac) => ac.account.id === account?.id && ac.isPrimary)
    )?.simplifiedName ?? undefined;
  const secondaryContact =
    goalContacts?.find((c) =>
      c.accountContacts.find((ac) => ac.account.id === account?.id && ac.isSecondary)
    )?.simplifiedName ?? undefined;

  switch (value) {
    case OWNER_PRIMARY:
      name = shortenName(primaryContact) || 'N/A';
      break;
    case OWNER_SECONDARY:
      name = shortenName(secondaryContact) || 'N/A';
      break;
    case OWNER_JOINT:
      name = `${shortenName(primaryContact)} & ${shortenName(secondaryContact)}`;
      break;
    case OWNER_FAMILY:
      name = OWNER_FAMILY_LABEL;
      break;
    case OWNER_TRUST:
      name = trustName;
      break;
    default:
      name = '';
      break;
  }
  return name;
};

export const calculateAge = (birthdayStr: string, todaysDate?: Date) => {
  const today = todaysDate || newUTCDate();
  const diff = today.getTime() - newUTCDate(birthdayStr).getTime();
  return Math.floor(diff / YEAR_IN_MILISECS);
};

export interface convertDomainToRangeProps {
  value: number;
  domainMax: number;
  domainMin: number;
  rangeMax: number;
  rangeMin: number;
}

export const convertDomainToRange = ({
  value,
  domainMax,
  domainMin,
  rangeMax,
  rangeMin,
}: convertDomainToRangeProps): number => {
  const rangeVal = (value - domainMin) / (domainMax - domainMin);
  return (rangeMax - rangeMin) * rangeVal + rangeMin;
};

type GoalUpdateWithRequired = Partial<UpdateGoalVariables> &
  Required<Pick<UpdateGoalVariables, 'drawdownRate' | 'targetCapital' | 'targetIncome'>>;

export const getFilteredGoalFormValues = (
  values?: GoalFormValues
): GoalUpdateWithRequired => {
  const drawdownRateInt = Number(values?.drawdownRate ?? values?.incomeRate) || 0;
  const targetCapitalInt = Number(values?.targetCapital) || 0;
  const targetIncomeInt = Number(values?.targetIncome) || 0;
  const targetAgeInt = Number(values?.targetAge) || 0;
  const targetYearsInt = Number(values?.targetYears) || 0;
  const maybeName = values?.name ?? values?.title;
  const maybeDescription = values?.description ?? '';

  const filtered: GoalUpdateWithRequired = {
    drawdownRate: drawdownRateInt,
    targetCapital: targetCapitalInt,
    targetIncome: targetIncomeInt,

    ...(values?.contactId && {contactId: validateUUID(values.contactId)}),
    ...(maybeName && {name: cleanForCuro(maybeName)}),
    ...(values ? {description: cleanForCuro(maybeDescription)} : {}),

    ...(values?.goalType && {goalType: values.goalType}),
    ...(values?.ownership && {ownership: values.ownership}),
    ...(values?.planningObjective && {planningObjective: values.planningObjective}),
    ...(values?.planningObjectivePurpose && {
      planningObjectivePurpose: values.planningObjectivePurpose,
    }),
    ...(values?.objectiveType !== undefined && {objectiveType: values.objectiveType}),
    ...(values?.priority !== undefined && {priority: values.priority}),
    ...(values?.secondaryContactId !== undefined && {
      secondaryContactId: values.secondaryContactId,
    }),
    ...(values?.statusAtReview !== undefined && {statusAtReview: values.statusAtReview}),

    ...(values
      ? {targetEndDate: getISODateOrUndefined(values.targetEndDate ?? null)}
      : {}),

    targetAge: targetAgeInt,
    targetYears: targetYearsInt,
  };

  return filtered;
};
